import { createContext, useContext } from "react";

/**
 * How a screen made of sections arranges them: `single` is the centred reading column, `loose` and
 * `dense` pack the sections into a grid with fewer or more columns. The grid is only applied on wide
 * screens (see `SectionLayout.module.css`), so a stored `loose` or `dense` choice falls back to a
 * single column on a narrow one.
 */
export type SectionLayoutMode = "single" | "loose" | "dense";

export const sectionLayoutModes: readonly SectionLayoutMode[] = ["single", "loose", "dense"];

/** Per-viewer convenience only: the choice is remembered on this browser and never sent anywhere. */
export const sectionLayoutStorageKey = "aia-portal.sectionLayout";

export interface SectionLayoutContextValue {
  mode: SectionLayoutMode;
  setMode: (mode: SectionLayoutMode) => void;
}

/** Outside a {@link SectionLayoutProvider} the layout is always the single column and cannot be changed. */
export const SectionLayoutContext = createContext<SectionLayoutContextValue>({ mode: "single", setMode: () => undefined });

/**
 * The viewer's current section layout, shared by the layout switcher and every section grid beneath
 * the provider.
 */
export function useSectionLayout(): SectionLayoutContextValue {
  return useContext(SectionLayoutContext);
}

/**
 * Reads the remembered layout. Storage can be blocked or unavailable (private windows, cleared site
 * data), so any failure falls back to the single column.
 */
export function readStoredSectionLayout(): SectionLayoutMode {
  try {
    const stored = window.localStorage.getItem(sectionLayoutStorageKey);
    return sectionLayoutModes.find((mode) => mode === stored) ?? "single";
  } catch {
    return "single";
  }
}

/** Remembers the layout for the next visit. A failed write only means the choice lasts for this visit. */
export function storeSectionLayout(mode: SectionLayoutMode): void {
  try {
    window.localStorage.setItem(sectionLayoutStorageKey, mode);
  } catch {
    // Storage is unavailable; the in-memory choice still applies.
  }
}
